/** @jsx jsx */
import React from 'react';
import { cssMap, jsx } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { EditButton as EditButtonDI } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/ui/shortcut/edit-button/main.tsx';
import { SyncButton as SyncButtonDI } from '@atlassian/jira-navigation-apps-shortcuts-sidebar/src/ui/shortcut/sync-button/main.tsx';
import {
	EMOJI_REGEX,
	LINK_TYPE,
	REPO_TYPE,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/common/constants.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { cssFn as hoverIconAfter } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/utils/css-fn-utils/hover-icon-after-css-fn.tsx';
import { messages } from './messages.tsx';
import type { ShortcutProps } from './types.tsx';

const styles = cssMap({
	emojiWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '24px',
		height: '24px',
		maxWidth: '24px',
		maxHeight: '24px',
	},
	emojiPlaceholder: {
		font: token('font.heading.large'),
		filter: 'filter',
	},
});

export const Shortcut = ({
	EditButton = EditButtonDI,
	MenuItem = MenuItemDI,
	SyncButton = SyncButtonDI,
	openEditItem,
	shortcut,
	// @ts-expect-error - TS2339 - Property 'useIntl' does not exist on type 'ShortcutProps'.
	useIntl = useIntlDI,
}: ShortcutProps) => {
	const {
		before: DefaultIconBefore,
		canEdit,
		href,
		id: shortcutId,
		onEditClick,
		onSyncClick,
		shortcutType,
		showSync,
		text,
		editButtonId,
	} = shortcut;
	const { formatMessage } = useIntl();

	let iconBefore = DefaultIconBefore ? <DefaultIconBefore /> : null;
	let shortcutText = text || '';

	if (shortcutType === LINK_TYPE && shortcutText) {
		const match = shortcutText.match(EMOJI_REGEX);
		if (match) {
			const [emoji] = match;
			const linkText = shortcutText.substr(emoji.length).trim();
			if (linkText) {
				shortcutText = linkText;
			}
			if (emoji) {
				iconBefore = (
					<span css={styles.emojiWrapper}>
						<span css={styles.emojiPlaceholder}>{emoji}</span>
					</span>
				);
			}
		}
	}

	const iconAfter = (() => {
		if (showSync === true && onSyncClick) {
			return (
				<SyncButton id={shortcutId} onClick={onSyncClick} label={formatMessage(messages.sync)} />
			);
		}
		if (canEdit === true) {
			let onClick = openEditItem;

			// We do not edit repository shortcuts on our own, instead we allow another plugin doing it.
			// Till the moment that plugin gets initialized we won't be rendering edit icon.
			if (shortcutType === REPO_TYPE) {
				if (!onEditClick) {
					return null;
				}
				onClick = onEditClick;
			}

			const ariaLabel =
				shortcutText !== ''
					? formatMessage(messages.editWithShortcutText, { shortcutText })
					: formatMessage(messages.edit);

			return (
				<EditButton id={editButtonId} shortcutId={shortcutId} onClick={onClick} label={ariaLabel} />
			);
		}
		return null;
	})();

	return (
		<Tooltip tag="span" content={shortcutText} position="bottom">
			<MenuItem
				analytics={{ itemId: `shortcut--item-${shortcutId}` }}
				iconAfter={iconAfter}
				iconBefore={iconBefore}
				href={href}
				target="_blank"
				cssFn={hoverIconAfter}
			>
				{shortcutText}
			</MenuItem>
		</Tooltip>
	);
};
